<script setup>
import useApp from "../composables/useApp";
import { computed } from "vue";

const props = defineProps({
    reminderData: {
        type: [Array, Object],
        required: true,
    }
});

const { useSimpleBanner } = useApp();

useSimpleBanner.value = true;

const os = navigator.userAgent;
//detect operating system based on user agent
const isMac = os.indexOf("Mac") > -1;
const isWindows = os.indexOf("Windows") > -1;
const isAndroid = os.indexOf("Android") > -1;
const isOther = !isMac && !isWindows && !isAndroid;
const isIos = os.match(/(iPad|iPhone|iPod)/g);
const isMobile = isIos || isAndroid;

const trademark = computed(() => {
    if (props.reminderData.productName.toLowerCase() === 'recombitek') {
        return "®"
    }

    if (props.reminderData.productName.toLowerCase() === 'purevax') {
        return "™"
    }

    if (props.reminderData.productName.toLowerCase() === 'nexgard combo') {
        return document.documentElement.lang === "fr" ? "MC" : "™"
    } else {
        return "®"
    }
});
</script>

<template>
    <section class="-mx-8 px-3 my-8">
        <div class="p-8 bg-bi-light-gray mb-12">
            <p v-html="$t('download.headline')"
                class="text-center font-bi-head-bold text-lg sm:text-2xl text-bi-dark-green mb-6">
            </p>

            <div class="flex flex-wrap items-start gap-4 sm:gap-8 justify-center">
                <div class="max-w-[100px]" v-if="isOther || isMac || isWindows">
                    <a :href="props.reminderData.webcalLink" target="_blank" :title="$t('home.calendar.ios.striphtml')"
                        :aria-label="$t('home.calendar.ios.striphtml')"
                        class="flex items-center justify-center aspect-square w-[89px] border-solid bg-bi-light-gray border-2 border-bi-accent-green rounded-md mx-auto mb-3 hover:border-2 hover:border-bi-dark-green hover:shadow-lg transition-all duration-100 ease-linear">
                        <img src="../../images/calendar-ios.png" :alt="$t('home.calendar.ios.striphtml')" width="64"
                            height="64" />
                    </a>
                    <span v-html="$t('home.calendar.ios')" class="block text-sm text-center text-bi-dark-green"></span>
                </div>

                <div class="max-w-[100px]" v-if="isOther || isAndroid || isMac || isWindows">
                    <a :href="'https://calendar.google.com/calendar/u/0/r?cid=' + props.reminderData.webcalLink"
                        target="_blank" :title="$t('home.calendar.google.striphtml')"
                        :aria-label="$t('home.calendar.google.striphtml')"
                        class="flex items-center justify-center aspect-square w-[89px] border-solid bg-bi-light-gray border-2 border-bi-accent-green rounded-md mx-auto mb-3 hover:border-2 hover:border-bi-dark-green hover:shadow-lg transition-all duration-100 ease-linear">
                        <img src="../../images/calendar-google.png" :alt="$t('home.calendar.google.striphtml')" width="64"
                            height="64" />
                    </a>
                    <span v-html="$t('home.calendar.google')"
                        class="block text-sm text-center text-bi-dark-green"></span>
                </div>

                <template v-if="!isMobile">
                    <div class="max-w-[100px]" v-if="isOther || isAndroid || isMac || isWindows">
                        <a :href="'https://outlook.office.com/calendar/addfromweb?url=' + props.reminderData.httpsLink"
                            target="_blank" :title="$t('home.calendar.outlook.web.striphtml')"
                            :aria-label="$t('home.calendar.outlook.web.striphtml')"
                            class="flex items-center justify-center aspect-square w-[89px] border-solid bg-bi-light-gray border-2 border-bi-accent-green rounded-md mx-auto mb-3 hover:border-2 hover:border-bi-dark-green hover:shadow-lg transition-all duration-100 ease-linear">
                            <img src="../../images/calendar-outlook.png" :alt="$t('home.calendar.outlook.web.striphtml')"
                                width="64" height="64" />
                        </a>
                        <span v-html="$t('home.calendar.outlook.web')"
                            class="block text-sm text-center text-bi-dark-green"></span>
                    </div>
                </template>

                <div class="max-w-[100px]" v-if="isOther || isWindows">
                    <a :href="props.reminderData.downloadReminderLink" target="_blank"
                        :title="$t('home.calendar.outlook.striphtml')"
                        :aria-label="$t('home.calendar.outlook.striphtml')"
                        class="flex items-center justify-center aspect-square w-[89px] border-solid bg-bi-light-gray border-2 border-bi-accent-green rounded-md mx-auto mb-3 hover:border-2 hover:border-bi-dark-green hover:shadow-lg transition-all duration-100 ease-linear">
                        <img src="../../images/calendar-outlook.png" :alt="$t('home.calendar.outlook.striphtml')" width="64"
                            height="64" />
                    </a>
                    <span v-html="$t('home.calendar.outlook')"
                        class="block text-sm text-center text-bi-dark-green"></span>
                </div>

                <div class="max-w-[100px]" v-if="isOther || isAndroid || isMac || isWindows">
                    <a :href="props.reminderData.downloadReminderLink" target="_blank"
                        :title="$t('home.calendar.other.striphtml')" :aria-label="$t('home.calendar.other.striphtml')"
                        class="flex items-center justify-center aspect-square w-[89px] border-solid bg-bi-light-gray border-2 border-bi-accent-green rounded-md mx-auto mb-3 hover:border-2 hover:border-bi-dark-green hover:shadow-lg transition-all duration-100 ease-linear">
                        <img src="../../images/calendar-other.png" :alt="$t('home.calendar.other.striphtml')" width="64"
                            height="64" />
                    </a>
                    <span v-html="$t('home.calendar.other')"
                        class="block text-sm text-center text-bi-dark-green"></span>
                </div>
            </div>
        </div>

        <p class="text-bi-dark-green text-xl text-center mx-auto mb-8">
            <template v-if="['Recombitek', 'Purevax'].includes(props.reminderData.productName)">
                <span v-html="$t('email.vaccination', {
                                name: props.reminderData.petName,
                                product: props.reminderData.productName,
                                date: props.reminderData.date,
                                trademark: trademark})"
                >
                </span>
            </template>

            <template v-else>
                {{ $t('download.your') }} <strong class="break-keep whitespace-nowrap">{{
                    props.reminderData.productName
                }}{{ trademark }}</strong> {{ $t('download.start') }} <strong class="break-keep whitespace-nowrap">{{
                        props.reminderData.date }}</strong>. <br>
            </template>

            <template
                v-if="!['NexGard', 'NexGard Spectra', 'Heartgard', 'NexGard Combo', 'Recombitek', 'Purevax'].includes(props.reminderData.productName)">
                {{ $t('download.bottleOf') }} <strong>{{
                    props.reminderData.productName }}{{ trademark }}</strong> {{ $t('download.willLast') }}
                <strong>{{
                    props.reminderData.daysToLast }}</strong>
                {{
                    $t('download.days', props.reminderData.daysToLast)
                }}.
            </template>
        </p>

        <p class="text-bi-dark-green text-center text-2xl font-bi-head-bold mb-8">
            {{ $t('download.easy') }}
        </p>

        <v-download-calendar-accordion></v-download-calendar-accordion>

        <div class="flex justify-center mt-12 mb-4">
            <a href="/" :title="$t('button.generate.another')" :aria-label="$t('button.generate.another')"
                class="inline-flex items-center rounded-md border-2 border-solid border-bi-dark-green hover:border-bi-accent-green px-5 py-4 text-lg font-bi-text-bold text-bi-dark-green shadow-sm hover:opacity-90 focus:outline-none">
                {{ $t('button.generate.another') }}
            </a>
        </div>
    </section>
</template>
